import { useEffect, useRef, useState } from 'react';
import { Plus, Video, Mic, BookOpen, FlaskConical, Link2, FileText, Presentation, Code2, Film, StickyNote, Radio, ChevronRight, Pencil, Trash2, Briefcase, X, Workflow, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';

import { TopicAboutEditor } from './TopicAboutEditor';
import { EditorialReleaseControl } from './EditorialReleaseControl';
import { SessionForm } from './SessionForm';
import { ResourceForm } from './ResourceForm';
import { AssessmentForm } from './AssessmentForm';
import { ConfirmDialog } from './ConfirmDialog';
import { MCQQuestionBuilder } from './MCQQuestionBuilder';
import { AssessmentSubmissions } from './AssessmentSubmissions';
import { VisualizationEditor, type VisualizationEditorHandle } from './VisualizationEditor';

import { Button } from '@/components/ui/button';
import { PreviewAsStudentButton } from '@/components/PreviewAsStudentButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  trainerContentApi,
  type CurriculumTopic,
  type TopicSession,
  type TopicResource,
  type TopicAssessment,
  type UpsertSessionRequest,
  type UpsertResourceRequest,
  type UpsertAssessmentRequest,
} from '@/lib/api/trainer';
import { cn } from '@/lib/cn';

type DeleteTarget =
  | { type: 'session'; id: string; label: string }
  | { type: 'resource'; id: string; label: string }
  | { type: 'assessment'; id: string; label: string };

/**
 * Even at `h-screen w-screen`, the dialog still shares the viewport with the
 * browser's own tab bar/URL bar — a trainer building a multi-block
 * visualization wants that space back too. Mirrors `Player`'s own
 * `useFullscreen` (same `document.fullscreenElement` pattern), scoped here
 * to the visualization editor's dialog instead.
 */
function useFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [ref]);
  const toggle = () => {
    if (document.fullscreenElement === ref.current) document.exitFullscreen();
    else ref.current?.requestFullscreen();
  };
  return { isFullscreen, toggle };
}

type DialogMode =
  | { kind: 'add-session' }
  | { kind: 'edit-session'; session: TopicSession }
  | { kind: 'add-resource' }
  | { kind: 'edit-resource'; resource: TopicResource }
  | { kind: 'add-assessment' }
  | { kind: 'edit-assessment'; assessment: TopicAssessment }
  | null;

interface Props {
  batchId: string;
  topicId: string;
  topic?: CurriculumTopic;
  onTopicUpdated?: (updated: CurriculumTopic) => void;
  onEditTopic?: () => void;
  onDeleteTopic?: () => void;
}

const TOPIC_TYPE_META: Record<string, { icon: React.ReactNode; label: string }> = {
  lecture: { icon: <Video className="h-3 w-3" />, label: 'Lecture' },
  lab:     { icon: <FlaskConical className="h-3 w-3" />, label: 'Lab' },
  project: { icon: <Briefcase className="h-3 w-3" />, label: 'Project' },
};

const SESSION_TYPE_META: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  live_class:   { icon: <Video className="h-3 w-3" />,       label: 'Live',         color: 'bg-[#fff1ec] text-[#ff5a1f] dark:bg-blue-950/30 dark:text-blue-400' },
  recorded:     { icon: <Film className="h-3 w-3" />,        label: 'Recorded',     color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400' },
  office_hours: { icon: <Mic className="h-3 w-3" />,         label: 'Office Hours', color: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
  lab:          { icon: <FlaskConical className="h-3 w-3" />, label: 'Lab',          color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' },
};

const SESSION_STATUS_META: Record<string, { label: string; color: string; dot?: boolean }> = {
  scheduled: { label: 'Scheduled', color: 'bg-[#fff1ec] text-[#ff5a1f] dark:bg-blue-950/30 dark:text-blue-400' },
  live:      { label: 'Live now',  color: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400', dot: true },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400' },
};

const RESOURCE_TYPE_META: Record<string, { icon: React.ReactNode; color: string }> = {
  link:      { icon: <Link2 className="h-3 w-3" />,        color: 'bg-[#fff1ec] text-[#ff5a1f] dark:bg-blue-950/30 dark:text-blue-400' },
  pdf:       { icon: <FileText className="h-3 w-3" />,      color: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
  slides:    { icon: <Presentation className="h-3 w-3" />,  color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' },
  code_repo: { icon: <Code2 className="h-3 w-3" />,         color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  recording: { icon: <Film className="h-3 w-3" />,          color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400' },
  notes:     { icon: <StickyNote className="h-3 w-3" />,    color: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
};

const ASSESSMENT_TYPE_META: Record<string, { label: string; color: string }> = {
  mcq_quiz:           { label: 'MCQ Quiz',    color: 'bg-[#fff1ec] text-[#ff5a1f]' },
  coding_challenge:   { label: 'Coding',      color: 'bg-green-50 text-green-700' },
  project_assignment: { label: 'Assignment',  color: 'bg-purple-50 text-purple-700' },
};

const actionBtn = 'inline-flex items-center gap-1 rounded border border-[#ececec] px-2 py-1 text-xs font-medium text-[#6b7280] hover:text-[#111827] hover:bg-[#fafafa] transition-colors';
const deleteBtn = 'inline-flex items-center gap-1 rounded border border-[#ececec] px-2 py-1 text-xs font-medium text-[#6b7280] hover:text-destructive hover:border-destructive/50 hover:bg-destructive/5 transition-colors';

export function TopicContentEditor({ batchId, topicId, topic, onTopicUpdated, onEditTopic, onDeleteTopic }: Props) {
  const [sessions,    setSessions]    = useState<TopicSession[]>([]);
  const [resources,   setResources]   = useState<TopicResource[]>([]);
  const [assessments, setAssessments] = useState<TopicAssessment[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [dialog,      setDialog]      = useState<DialogMode>(null);
  const [openSections, setOpenSections] = useState(new Set(['about', 'sessions', 'resources', 'assessments', 'visualization']));
  const [expandedMCQBuilder,   setExpandedMCQBuilder]   = useState<string | null>(null);
  const [expandedSubmissions,  setExpandedSubmissions]  = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DeleteTarget | null>(null);
  const [visualizationEditorOpen, setVisualizationEditorOpen] = useState(false);
  const visualizationEditorRef = useRef<VisualizationEditorHandle>(null);
  const visualizationDialogRef = useRef<HTMLDivElement>(null);
  const { isFullscreen: visualizationIsFullscreen, toggle: toggleVisualizationFullscreen } =
    useFullscreen(visualizationDialogRef);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, r, a] = await Promise.all([
        trainerContentApi.listSessions(batchId, topicId),
        trainerContentApi.listResources(batchId, topicId),
        trainerContentApi.listAssessments(batchId, topicId),
      ]);
      setSessions(s.data);
      setResources(r.data);
      setAssessments(a.data);
    } catch {
      toast.error('Failed to load topic content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [topicId]);

  const toggleSection = (key: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // --- Session handlers ---
  const handleCreateSession = async (req: UpsertSessionRequest) => {
    await trainerContentApi.createSession(batchId, topicId, req);
    toast.success('Session added');
    setDialog(null);
    reload();
  };

  const handleUpdateSession = async (req: UpsertSessionRequest) => {
    if (dialog?.kind !== 'edit-session') return;
    await trainerContentApi.updateSession(batchId, topicId, dialog.session.id, req);
    toast.success('Session updated');
    setDialog(null);
    reload();
  };

  // --- Resource handlers ---
  const handleCreateResource = async (req: UpsertResourceRequest) => {
    await trainerContentApi.createResource(batchId, topicId, req);
    toast.success('Resource added');
    setDialog(null);
    reload();
  };

  const handleUpdateResource = async (req: UpsertResourceRequest) => {
    if (dialog?.kind !== 'edit-resource') return;
    await trainerContentApi.updateResource(batchId, topicId, dialog.resource.id, req);
    toast.success('Resource updated');
    setDialog(null);
    reload();
  };

  // --- Assessment handlers ---
  const handleCreateAssessment = async (req: UpsertAssessmentRequest) => {
    await trainerContentApi.createAssessment(batchId, topicId, req);
    toast.success('Assessment added');
    setDialog(null);
    reload();
  };

  const handleUpdateAssessment = async (req: UpsertAssessmentRequest) => {
    if (dialog?.kind !== 'edit-assessment') return;
    await trainerContentApi.updateAssessment(batchId, topicId, dialog.assessment.id, req);
    toast.success('Assessment updated');
    setDialog(null);
    reload();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      if (pendingDelete.type === 'session') {
        await trainerContentApi.deleteSession(batchId, topicId, pendingDelete.id);
        toast.success('Session deleted');
      } else if (pendingDelete.type === 'resource') {
        await trainerContentApi.deleteResource(batchId, topicId, pendingDelete.id);
        toast.success('Resource deleted');
      } else {
        await trainerContentApi.deleteAssessment(batchId, topicId, pendingDelete.id);
        toast.success('Assessment deleted');
      }
      reload();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setPendingDelete(null);
    }
  };

  const dialogTitle =
    dialog?.kind === 'add-session'    ? 'Add session'    :
    dialog?.kind === 'edit-session'   ? 'Edit session'   :
    dialog?.kind === 'add-resource'   ? 'Add resource'   :
    dialog?.kind === 'edit-resource'  ? 'Edit resource'  :
    dialog?.kind === 'add-assessment' ? 'Add assessment' :
    dialog?.kind === 'edit-assessment'? 'Edit assessment': '';

  const topicTypeMeta = topic ? (TOPIC_TYPE_META[topic.topic_type] ?? TOPIC_TYPE_META['lecture']) : null;

  return (
    <>
      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.type ?? ''}?`}
        description={`"${pendingDelete?.label}" will be permanently deleted.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Form dialogs */}
      <Dialog open={dialog !== null} onOpenChange={(open) => { if (!open) setDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          {dialog?.kind === 'add-session' && (
            <SessionForm onSave={handleCreateSession} onCancel={() => setDialog(null)} />
          )}
          {dialog?.kind === 'edit-session' && (
            <SessionForm initial={dialog.session} onSave={handleUpdateSession} onCancel={() => setDialog(null)} />
          )}
          {dialog?.kind === 'add-resource' && (
            <ResourceForm onSave={handleCreateResource} onCancel={() => setDialog(null)} />
          )}
          {dialog?.kind === 'edit-resource' && (
            <ResourceForm initial={dialog.resource} onSave={handleUpdateResource} onCancel={() => setDialog(null)} />
          )}
          {dialog?.kind === 'add-assessment' && (
            <AssessmentForm onSave={handleCreateAssessment} onCancel={() => setDialog(null)} />
          )}
          {dialog?.kind === 'edit-assessment' && (
            <AssessmentForm initial={dialog.assessment} onSave={handleUpdateAssessment} onCancel={() => setDialog(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Topic header */}
      {topic && (
        <div className="sticky top-0 z-10 bg-white border-b border-[#ececec] px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold leading-snug">{topic.title}</h2>
              <div className="flex items-center gap-2 mt-1.5">
                {topicTypeMeta && (
                  <span className="flex items-center gap-1 rounded-full bg-[#f3f4f6] px-2.5 py-0.5 text-[11px] font-medium text-[#6b7280]">
                    {topicTypeMeta.icon}
                    {topicTypeMeta.label}
                  </span>
                )}
                {topic.estimated_hours && (
                  <span className="text-xs text-[#6b7280]">{topic.estimated_hours}h estimated</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <PreviewAsStudentButton batchId={batchId} topicId={topicId} className={actionBtn} />
              {onEditTopic && (
                <button onClick={onEditTopic} className={actionBtn}>
                  <Pencil className="h-3 w-3" />Edit
                </button>
              )}
              {onDeleteTopic && (
                <button onClick={onDeleteTopic} className={deleteBtn}>
                  <Trash2 className="h-3 w-3" />Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="p-6 text-sm text-[#6b7280]">Loading…</p>
      ) : (
        <div className="divide-y divide-[#ececec]">

          {/* ── About ── */}
          {topic && (
            <Section
              sectionKey="about"
              title="About"
              subtitle="shown to students"
              open={openSections.has('about')}
              onToggle={() => toggleSection('about')}
            >
              <div className="px-6 pt-4 pb-5">
                <TopicAboutEditor key={topic.id} batchId={batchId} topic={topic} onSaved={(u) => onTopicUpdated?.(u)} />
              </div>
            </Section>
          )}

          {/* ── Sessions ── */}
          <Section
            sectionKey="sessions"
            title="Sessions"
            count={sessions.length}
            open={openSections.has('sessions')}
            onToggle={() => toggleSection('sessions')}
            onAdd={() => setDialog({ kind: 'add-session' })}
            addLabel="Add session"
          >
            <div className="px-6 pt-4 pb-5">
              {sessions.length === 0 ? (
                <EmptyState message="No sessions scheduled yet" action="Schedule a session" onAction={() => setDialog({ kind: 'add-session' })} />
              ) : (
                <ul className="space-y-2">
                  {sessions.map((s) => {
                    const typeMeta   = SESSION_TYPE_META[s.session_type ?? 'live_class'];
                    const statusMeta = SESSION_STATUS_META[s.status ?? 'scheduled'];
                    return (
                      <li key={s.id} className="flex items-center justify-between rounded-lg border border-[#ececec] px-3 py-2 hover:bg-[#fafafa]/20 transition-colors">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={cn('flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0', typeMeta.color)}>
                            {typeMeta.icon} {typeMeta.label}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium leading-snug">{s.title}</p>
                              <span className={cn('flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0', statusMeta.color)}>
                                {statusMeta.dot && <Radio className="h-2.5 w-2.5 animate-pulse" />}
                                {statusMeta.label}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-0.5">
                              {s.scheduled_at && (
                                <span className="text-xs text-[#6b7280]">
                                  {new Date(s.scheduled_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}{' '}
                                  {new Date(s.scheduled_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                              {s.duration_mins && <span className="text-xs text-[#6b7280]">{s.duration_mins} min</span>}
                              {s.attendance_required && (
                                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400">Required</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 ml-3">
                          {s.meeting_url && (
                            <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" className={cn(actionBtn, 'text-[#ff5a1f] border-[#ffd9c7] hover:bg-[#fff1ec]')}>Join</a>
                          )}
                          {s.recording_url && (
                            <a href={s.recording_url} target="_blank" rel="noopener noreferrer" className={cn(actionBtn, 'text-purple-600 border-purple-200 hover:bg-purple-50')}>Recording</a>
                          )}
                          <button onClick={() => setDialog({ kind: 'edit-session', session: s })} className={actionBtn}><Pencil className="h-3 w-3" />Edit</button>
                          <button onClick={() => setPendingDelete({ type: 'session', id: s.id, label: s.title })} className={deleteBtn}><Trash2 className="h-3 w-3" />Delete</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Section>

          {/* ── Resources ── */}
          <Section
            sectionKey="resources"
            title="Resources"
            count={resources.length}
            open={openSections.has('resources')}
            onToggle={() => toggleSection('resources')}
            onAdd={() => setDialog({ kind: 'add-resource' })}
            addLabel="Add resource"
          >
            <div className="px-6 pt-4 pb-5">
              {resources.length === 0 ? (
                <EmptyState message="No resources added yet" action="Add a resource" onAction={() => setDialog({ kind: 'add-resource' })} />
              ) : (
                <ul className="space-y-2">
                  {resources.map((res) => {
                    const meta = RESOURCE_TYPE_META[res.type] ?? { icon: <BookOpen className="h-3 w-3" />, color: 'bg-[#f3f4f6] text-[#6b7280]' };
                    return (
                      <li key={res.id} className="flex items-center justify-between rounded-lg border border-[#ececec] px-3 py-2 hover:bg-[#fafafa]/20 transition-colors">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={cn('flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0', meta.color)}>
                            {meta.icon} {res.type}
                          </span>
                          <div className="min-w-0">
                            <a href={res.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline">
                              {res.title}
                            </a>
                            {res.description && <p className="mt-0.5 text-xs text-[#6b7280] line-clamp-1">{res.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                          {res.is_required && (
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400">Required</span>
                          )}
                          <button onClick={() => setDialog({ kind: 'edit-resource', resource: res })} className={actionBtn}><Pencil className="h-3 w-3" />Edit</button>
                          <button onClick={() => setPendingDelete({ type: 'resource', id: res.id, label: res.title })} className={deleteBtn}><Trash2 className="h-3 w-3" />Delete</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Section>

          {/* ── Assessments ── */}
          <Section
            sectionKey="assessments"
            title="Assessments"
            count={assessments.length}
            open={openSections.has('assessments')}
            onToggle={() => toggleSection('assessments')}
            onAdd={() => setDialog({ kind: 'add-assessment' })}
            addLabel="Add assessment"
          >
            <div className="px-6 pt-4 pb-5">
              {/* Sits with the assessments because that is where this topic's
                  problems are: topic_assessments.problem_slug is exactly what
                  the release unlocks. */}
              {topic && (
                <EditorialReleaseControl
                  batchId={batchId}
                  topic={topic}
                  onChanged={(u) => onTopicUpdated?.(u)}
                />
              )}
              {assessments.length === 0 ? (
                <EmptyState message="No assessments yet" action="Create an assessment" onAction={() => setDialog({ kind: 'add-assessment' })} />
              ) : (
                <ul className="space-y-2">
                  {assessments.map((a) => {
                    const meta  = ASSESSMENT_TYPE_META[a.type] ?? { label: a.type, color: 'bg-[#f3f4f6] text-[#6b7280]' };
                    const label = a.type === 'project_assignment' && a.assignment_kind === 'project' ? 'Project' : meta.label;
                    return (
                      <li key={a.id} className="rounded-lg border border-[#ececec]">
                        <div className="flex items-start justify-between px-3 py-2.5 hover:bg-[#fafafa]/20 transition-colors rounded-lg">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <span className={cn('mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0', meta.color)}>{label}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{a.title}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                <span className="text-xs text-[#6b7280]">{a.max_score} pts</span>
                                {a.due_date && <span className="text-xs text-[#6b7280]">Due {a.due_date.slice(0, 10)}</span>}
                                {a.duration_mins && <span className="text-xs text-[#6b7280]">{a.duration_mins} min</span>}
                                {a.max_attempts && a.max_attempts > 1 && <span className="text-xs text-[#6b7280]">{a.max_attempts} attempts</span>}
                                {a.questions_per_attempt && <span className="text-xs text-[#6b7280]">{a.questions_per_attempt} Qs/attempt</span>}
                                {a.shuffle_questions && <span className="text-xs text-[#6b7280]">Shuffled</span>}
                                {a.difficulty && <span className="text-xs text-[#6b7280] capitalize">{a.difficulty}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0 ml-3">
                            {a.type === 'mcq_quiz' && (
                              <>
                                <button
                                  onClick={() => { setExpandedMCQBuilder(expandedMCQBuilder === a.id ? null : a.id); setExpandedSubmissions(null); }}
                                  className={cn(actionBtn, 'text-orange-600 border-orange-200 hover:bg-orange-50')}
                                >
                                  {expandedMCQBuilder === a.id ? 'Close' : 'Questions'}
                                </button>
                                <button
                                  onClick={() => { setExpandedSubmissions(expandedSubmissions === a.id ? null : a.id); setExpandedMCQBuilder(null); }}
                                  className={cn(actionBtn, 'text-purple-600 border-purple-200 hover:bg-purple-50')}
                                >
                                  {expandedSubmissions === a.id ? 'Close' : 'Submissions'}
                                </button>
                              </>
                            )}
                            <button onClick={() => setDialog({ kind: 'edit-assessment', assessment: a })} className={actionBtn}><Pencil className="h-3 w-3" />Edit</button>
                            <button onClick={() => setPendingDelete({ type: 'assessment', id: a.id, label: a.title })} className={deleteBtn}><Trash2 className="h-3 w-3" />Delete</button>
                          </div>
                        </div>
                        {a.type === 'mcq_quiz' && expandedMCQBuilder === a.id && (
                          <MCQQuestionBuilder assessmentID={a.id} assessmentTitle={a.title} />
                        )}
                        {a.type === 'mcq_quiz' && expandedSubmissions === a.id && (
                          <AssessmentSubmissions assessmentID={a.id} assessmentTitle={a.title} onClose={() => setExpandedSubmissions(null)} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Section>

          {/* ── Visualization ── */}
          {topic && (
            <Section
              sectionKey="visualization"
              title="Visualization"
              subtitle="step-script animation shown to students"
              open={openSections.has('visualization')}
              onToggle={() => toggleSection('visualization')}
            >
              <div className="px-6 pt-4 pb-5">
                {topic.has_source_topic ? (
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-[#ececec] bg-[#fafafa]/40 p-4">
                    <div className="flex items-start gap-3">
                      <Workflow className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#6b7280]" />
                      <div>
                        <p className="text-sm font-medium">Step-by-step visualization</p>
                        <p className="mt-0.5 text-xs text-[#6b7280]">
                          Build the animated walkthrough students see for this topic — form
                          fields, a tree/graph canvas, and a live preview, all in a dedicated
                          full-screen editor.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setVisualizationEditorOpen(true)}
                      className={cn(actionBtn, 'flex-shrink-0')}
                    >
                      Edit Visualization
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[#ececec] bg-[#fafafa]/40 p-4 text-xs text-[#6b7280]">
                    This topic was not forked from a curriculum template, so it cannot have a
                    visualization.
                  </div>
                )}
              </div>
            </Section>
          )}

        </div>
      )}

      {/* Full-screen visualization editor. Built on this codebase's existing
          shadcn Dialog (not a hand-rolled `fixed inset-0` overlay) so it gets
          Radix's focus trap, focus-on-open/restore-on-close, body scroll
          lock, background `aria-hidden`, and Escape handling for free —
          `[&>button]:hidden` hides Dialog's own default corner close button
          since it's a direct child of DialogContent, leaving only our own
          header close button (which routes through the guarded
          `requestClose()`) without a duplicate. `onOpenChange` never sets
          `visualizationEditorOpen` directly — ALL of Radix's own dismissal
          triggers (Escape, the hidden default close, outside-interact) are
          routed through `requestClose()` so an in-progress edit gets the
          same unsaved-changes guard regardless of how the user tries to
          close it; only `VisualizationEditor`'s own `onRequestClose` (called
          once the guard clears) actually flips the open state. */}
      {topic && (
        <Dialog
          open={visualizationEditorOpen}
          onOpenChange={(open) => {
            if (!open) visualizationEditorRef.current?.requestClose();
          }}
        >
          <DialogContent
            ref={visualizationDialogRef}
            className="left-0 top-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none data-[state=open]:!slide-in-from-left-0 data-[state=open]:!slide-in-from-top-0 data-[state=closed]:!slide-out-to-left-0 data-[state=closed]:!slide-out-to-top-0 [&>button]:hidden"
          >
            <DialogHeader className="flex-shrink-0 flex-row items-center justify-between space-y-0 border-b border-[#ececec] px-6 py-4 text-left">
              <div>
                <DialogTitle className="text-base font-semibold leading-snug">
                  {topic.title}
                </DialogTitle>
                <DialogDescription className="text-xs text-[#6b7280]">
                  Visualization editor
                </DialogDescription>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={toggleVisualizationFullscreen}
                  className="h-9 w-9 text-[#6b7280] hover:text-[#111827]"
                  aria-label={visualizationIsFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
                  title={visualizationIsFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
                >
                  {visualizationIsFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => visualizationEditorRef.current?.requestClose()}
                  className="h-9 w-9 text-[#6b7280] hover:text-[#111827]"
                  aria-label="Close visualization editor"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </DialogHeader>
            <div className="min-h-0 flex-1 p-6">
              <VisualizationEditor
                ref={visualizationEditorRef}
                batchId={batchId}
                topicId={topicId}
                hasSourceTopic={Boolean(topic.has_source_topic)}
                onRequestClose={() => setVisualizationEditorOpen(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────

interface SectionProps {
  sectionKey: string;
  title: string;
  subtitle?: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  addLabel?: string;
  children: React.ReactNode;
}

function Section({ title, subtitle, count, open, onToggle, onAdd, addLabel, children }: SectionProps) {
  return (
    <div>
      <div className="sticky top-[73px] z-[5] bg-white border-b border-[#ececec] flex items-center justify-between px-6 py-3">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 text-left"
        >
          <ChevronRight className={cn('h-4 w-4 text-[#6b7280] transition-transform', open && 'rotate-90')} />
          <span className="text-sm font-semibold">{title}</span>
          {subtitle && <span className="text-xs text-[#6b7280]">{subtitle}</span>}
          {count !== undefined && count > 0 && (
            <span className="rounded-full border border-[#ececec] bg-[#f3f4f6] px-2 py-0.5 text-[10px] font-semibold text-[#6b7280]">{count}</span>
          )}
        </button>
        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#ff5a1f] hover:bg-[#fff1ec] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        )}
      </div>
      {open && children}
    </div>
  );
}

function EmptyState({ message, action, onAction }: { message: string; action: string; onAction: () => void }) {
  return (
    <div className="flex flex-col items-center py-8 text-center gap-2">
      <p className="text-sm text-[#6b7280]">{message}</p>
      <button onClick={onAction} className="text-sm text-[#ff5a1f] hover:underline font-medium">
        {action}
      </button>
    </div>
  );
}
